// @flow
import * as React from "react";
import { Field, formValues } from "redux-form";
import { Form } from "semantic-ui-react";
import classnames from "classnames";

import {
  DIRECT_DEBIT_IBAN,
  DIRECT_DEBIT_BLZ_ACCOUNT_NUMBER,
} from "Commons/Components/FormComponents/components/PaymentForm/Constants";
import { InputField } from "./Fields";
import { isRequired } from "../misc/Validators";
import { isIban, isBankCode, isGermanAccountNumber } from "Commons/Utilities/validators";
import FormLabel from "Commons/Components/FormComponents/components/FormLabelWithDivider";
import { formatIban, normaliseIban } from "../misc/helpers";

type MethodProps = {|
  t: typeof TFunction,
  desiredPaymentMethod: typeof DIRECT_DEBIT_IBAN | typeof DIRECT_DEBIT_BLZ_ACCOUNT_NUMBER,
  validation: {|
    isAccountBankNumberValid?: boolean,
    isAccountNumberValid?: boolean,
    isIbanValid?: boolean,
  |},
  isOptional?: boolean,
  showWarning?: boolean,
  className: string,
  width: number,
  noLabel: boolean,
|};

let MethodSection = ({
  t,
  desiredPaymentMethod,
  showWarning,
  isOptional = false,
  className,
  width = 10,
  noLabel,
  validation: { isAccountBankNumberValid, isAccountNumberValid, isIbanValid },
}: MethodProps) => {
  const isAccountBankNumberValidExternally = () =>
    isAccountBankNumberValid === false ? t("validation:bankCodeIsInvalid") : undefined;

  const isAccountNumberValidExternally = () =>
    isAccountNumberValid === false ? t("validation:accountNumberIsInvalid") : undefined;

  const isIbanValidExternally = () => (isIbanValid === false ? t("validation:ibanIsInvalid") : undefined);

  if (desiredPaymentMethod === DIRECT_DEBIT_IBAN) {
    return (
      <>
        <Form.Group data-cy="iban" className={classnames(className)}>
          {!noLabel && <FormLabel divider id="accountIban" title={t("common:payment.accountIban")} />}
          <Form.Field width={width}>
            <Field
              name="accountIban"
              validate={isOptional ? [isIban] : [isRequired, isIban, isIbanValidExternally]}
              component={InputField}
              format={formatIban}
              normalize={normaliseIban}
              props={{
                showWarning,
                id: "accountIban",
                placeholder: t("common:payment.accountIban"),
                normalizeOnBlur: (value) => value.replace(/\s/g, ""),
              }}
            />
          </Form.Field>
        </Form.Group>
      </>
    );
  }

  if (desiredPaymentMethod === DIRECT_DEBIT_BLZ_ACCOUNT_NUMBER) {
    return (
      <>
        <Form.Group data-cy="bankCode" className={classnames(className)}>
          {!noLabel && <FormLabel divider id="accountBankNumber" title={t("common:payment.bankCode")} />}
          <Form.Field width={width}>
            <Field
              name="accountBankNumber"
              validate={isOptional ? [] : [isRequired, isAccountBankNumberValidExternally, ...isBankCode]}
              component={InputField}
              props={{
                showWarning,
                id: "accountBankNumber",
                placeholder: t("common:payment.bankCode"),
              }}
            />
          </Form.Field>
        </Form.Group>
        <Form.Group data-cy="accountNumber">
          {!noLabel && <FormLabel divider id="accountNumber" title={t("common:payment.accountNumber")} />}
          <Form.Field width={width}>
            <Field
              name="accountNumber"
              validate={isOptional ? [] : [isRequired, isAccountNumberValidExternally, ...isGermanAccountNumber]}
              component={InputField}
              props={{
                showWarning,
                id: "accountNumber",
                placeholder: t("common:payment.accountNumber"),
              }}
            />
          </Form.Field>
        </Form.Group>
      </>
    );
  }

  return null;
};

export const Method = formValues("desiredPaymentMethod")(MethodSection);
